import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CITY_COORDINATES, MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES } from "./serviceCatalog";

const normalizeCity = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
const getCityCoordinates = (cityName) => {
  const key = normalizeCity(cityName);
  return key ? (CITY_COORDINATES[key] || null) : null;
};
const getDistanceKm = (from, to) => {
  if (!from || !to) return null;
  const lat1 = Number(from.lat);
  const lon1 = Number(from.lng);
  const lat2 = Number(to.lat);
  const lon2 = Number(to.lng);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
  const toRadians = (value) => (Number(value) * Math.PI) / 180;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 6371 * c;
};
const getNearestCityOptionByCoords = (coords, options) => {
  if (!coords || !Array.isArray(options) || options.length === 0) return null;
  let nearest = null;
  options.forEach((option) => {
    const cityCoords = getCityCoordinates(option);
    if (!cityCoords) return;
    const distanceKm = getDistanceKm(coords, cityCoords);
    if (!Number.isFinite(distanceKm)) return;
    if (!nearest || distanceKm < nearest.distanceKm) {
      nearest = { city: option, distanceKm };
    }
  });
  return nearest;
};

const PETAAR_CONTACT = {
  address: "Petaar, Hsr layout, banglore, India - 560102",
  phone: "8050539777",
};

export default function SiteChrome({
  children,
  user,
  role,
  activePage = "browse",
  onBack,
  onHome,
  onConsultVetNow,
  onLogin,
  onSignupOwner,
  onSignupProvider,
  onOpenDashboard,
  onOpenServices,
  onOpenMarketplace,
  onOpenGrooming,
  onOpenBoarding,
  onAboutUs,
  onContactUs,
  onMyServices,
  onOpenLegal,
  onSearchServices,
  onOpenCategory,
  onSelectCity,
}) {
  const [searchText, setSearchText] = useState("");
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState("");
  const [locatingCity, setLocatingCity] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  const isMobile = viewportWidth < 760;
  const wasMobileRef = useRef(isMobile);


  const sortedCities = useMemo(
    () => [...MAJOR_INDIAN_CITIES].sort((a, b) => a.localeCompare(b)),
    [],
  );
  const serviceMenuCategories = useMemo(
    () => SERVICE_CATEGORIES.map((category) => category.name),
    [],
  );

  useEffect(() => {
    try {
      const storedCity = localStorage.getItem("petaar_selected_city") || "";
      if (storedCity) setSelectedCity(storedCity);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!selectedCity) return;
    try {
      localStorage.setItem("petaar_selected_city", selectedCity);
    } catch (_) {}
  }, [selectedCity]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (wasMobileRef.current !== isMobile) {
      setServicesMenuOpen(false);
      wasMobileRef.current = isMobile;
    }
  }, [isMobile]);

  const runTopSearch = () => {
    const query = String(searchText || "").trim();
    if (query && typeof onSearchServices === "function") {
      onSearchServices(query);
      return;
    }
    if (typeof onOpenServices === "function") {
      onOpenServices();
    } else if (typeof onHome === "function") {
      onHome();
    }
  };
  const openServiceCategory = useCallback((categoryName) => {
    if (typeof onOpenCategory === "function") {
      onOpenCategory(categoryName);
    } else if (typeof onSearchServices === "function") {
      onSearchServices(categoryName);
    } else if (typeof onOpenServices === "function") {
      onOpenServices();
    }
    setServicesMenuOpen(false);
  }, [onOpenCategory, onOpenServices, onSearchServices]);

  const navTabs = [
    { label: "Home", onClick: onHome },
    { label: "Our Services", onClick: onOpenServices || onHome, isServicesMenu: true },
    { label: "Marketplace", onClick: onOpenMarketplace || onHome },
    { label: "Vet on Call", onClick: onConsultVetNow },
  ];
  const showBackButton = String(activePage || "browse").trim() !== "browse";

  const applyCitySelection = useCallback((nextCity) => {
    const cityName = String(nextCity || "").trim();
    setSelectedCity(cityName);
    if (typeof onSelectCity === "function") {
      onSelectCity(cityName);
    } else if (typeof onOpenServices === "function") {
      onOpenServices();
    }
  }, [onOpenServices, onSelectCity]);

  const detectCurrentLocation = useCallback(() => {
    if (locatingCity) return;
    if (!navigator?.geolocation) {
      alert("Geolocation is not supported on this browser.");
      return;
    }
    setLocatingCity(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const currentCoords = {
            lat: Number(latitude),
            lng: Number(longitude),
          };
          const nearest = getNearestCityOptionByCoords(currentCoords, sortedCities);
          const matchedCity = nearest?.city || "";
          if (matchedCity) {
            applyCitySelection(matchedCity);
            alert(
              `Location detected near ${matchedCity}${
                Number.isFinite(nearest?.distanceKm) ? ` (${Math.round(nearest.distanceKm)} km)` : ""
              }.`,
            );
          } else {
            alert("Location detected, but city match not found. Please choose city manually.");
          }
        } catch (_) {
          alert("Could not detect your location. Please select city manually.");
        } finally {
          setLocatingCity(false);
        }
      },
      () => {
        setLocatingCity(false);
        alert("Location permission denied or unavailable.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }, [applyCitySelection, locatingCity, sortedCities]);

  useEffect(() => {
    if (selectedCity) return;
    if (!navigator?.permissions?.query) return;
    navigator.permissions
      .query({ name: "geolocation" })
      .then((status) => {
        if (status.state === "granted") {
          detectCurrentLocation();
        }
      })
      .catch(() => {});
  }, [detectCurrentLocation, selectedCity]);

  const footerColumns = [
    {
      title: "Petaar",
      links: [
        { label: "About Us", legalKey: "about" },
        { label: "Terms & Conditions", legalKey: "terms" },
        { label: "Privacy Policy", legalKey: "privacy" },
        { label: "Refund Policy", legalKey: "refund" },
      ],
    },
    {
      title: "For Partners",
      links: [
        { label: "Become a Partner", onClick: onSignupProvider },
        { label: "Partner Dashboard", onClick: onOpenDashboard },
        { label: "Consultation Program", onClick: onConsultVetNow },
      ],
    },
    {
      title: "Support",
      links: [
        { label: "Support", legalKey: "support" },
        { label: "Help Center", legalKey: "help" },
        { label: "Medical Disclaimer", legalKey: "medical" },
        { label: "Contact Us", legalKey: "contact" },
      ],
    },
  ];

  return (
    <div
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", overflowX: "hidden" }}
    >
      <header className="petaar-shell-header">
        <div className="petaar-topbar">
          <div
            className="petaar-topbar-brand"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            {showBackButton && (
              <button
                onClick={() => {
                  if (typeof onBack === "function") {
                    onBack();
                    return;
                  }
                  if (typeof onHome === "function") onHome();
                }}
                aria-label="Go back"
                style={{
                  width: isMobile ? 34 : 38,
                  height: isMobile ? 34 : 38,
                  borderRadius: 999,
                  border: "1px solid rgba(245, 197, 24, 0.42)",
                  background: "#fffef7",
                  color: "#111111",
                  cursor: "pointer",
                  fontSize: 18,
                  fontWeight: 800,
                  boxShadow: "var(--shadow)",
                }}
              >
                ←
              </button>
            )}
            <button
              onClick={onHome}
              className="petaar-topbar-logo"
            >
              <span className="petaar-logo-lockup">
                <img
                  src="/petaar-logo.jpeg"
                  alt="Petaar"
                  className="petaar-logo-wordmark"
                />
              </span>
            </button>
          </div>

          <div className="petaar-topbar-main">
            <div className="petaar-search-cluster">
              <div className="petaar-search-box">
                <button
                  onClick={runTopSearch}
                  aria-label="Search"
                  className="petaar-search-inline-action"
                >
                  ⌕
                </button>
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search services for your pet..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      runTopSearch();
                    }
                  }}
                />
              </div>
            </div>

            {!isMobile && (
              <div className="petaar-location-shell">
                <span className="petaar-location-icon">📍</span>
                <select
                  value={selectedCity}
                  onChange={(e) => applyCitySelection(e.target.value)}
                  className="petaar-location-select"
                >
                  <option value="">All Locations</option>
                  {sortedCities.map((cityName) => (
                    <option key={`top-city-${cityName}`} value={cityName}>{cityName}</option>
                  ))}
                </select>
              </div>
            )}

            {!isMobile && (
              <button
                onClick={detectCurrentLocation}
                disabled={locatingCity}
                className="petaar-button--secondary"
                style={{ whiteSpace: "nowrap", width: "auto", minHeight: 34, padding: "8px 12px", fontSize: 12 }}
              >
                {locatingCity ? "Locating..." : "Detect"}
              </button>
            )}

            {!isMobile && <div className="petaar-topbar-spacer" />}

            <div className="petaar-nav-actions" style={{ justifyContent: "flex-end" }}>
            <button
              onClick={onConsultVetNow}
              className="petaar-button--secondary"
              style={{ minHeight: 34, padding: "8px 12px", fontSize: isMobile ? 12 : 13 }}
            >
              Consult Vet
            </button>
            {!user ? (
              <>
                <button
                  onClick={onLogin}
                  className="petaar-button--secondary"
                  style={{ minHeight: 34, padding: "8px 12px", fontSize: isMobile ? 12 : 13 }}
                >
                  Login
                </button>
                <button
                  onClick={onSignupOwner}
                  className="petaar-button"
                  style={{ minHeight: 34, padding: "8px 12px", fontSize: isMobile ? 12 : 13 }}
                >
                  Signup
                </button>
              </>
            ) : (
              <button
                onClick={onOpenDashboard}
                className="petaar-button"
                style={{ minHeight: 34, padding: "8px 12px", fontSize: isMobile ? 12 : 13 }}
              >
                {role === "provider" ? "Provider Dashboard" : role === "admin" ? "Admin Dashboard" : "My Dashboard"}
              </button>
            )}
            <button
              onClick={onSignupProvider}
              className="petaar-button--accent"
              style={{ minHeight: 34, padding: "8px 12px", fontSize: isMobile ? 12 : 13 }}
            >
              For Vets
            </button>
          </div>
          </div>
        </div>

        <div
          className="petaar-subnav"
          style={{
            overflowX: isMobile ? "auto" : "visible",
            overflowY: "visible",
            position: "relative",
          }}
        >
          <div
            className="petaar-pill-tabs"
            style={{
              minWidth: isMobile ? "max-content" : "auto",
              overflowX: isMobile ? "auto" : "visible",
              overflowY: "visible",
            }}
          >
            {navTabs.map((tab) => (
              tab.isServicesMenu ? (
                <div
                  key={tab.label}
                  onMouseEnter={isMobile ? undefined : () => setServicesMenuOpen(true)}
                  onMouseLeave={isMobile ? undefined : () => setServicesMenuOpen(false)}
                  style={{ position: "relative", display: "inline-flex" }}
                >
                  <button
                    onClick={() => {
                      setServicesMenuOpen((prev) => !prev);
                    }}
                    onFocus={isMobile ? undefined : () => setServicesMenuOpen(true)}
                    className={`petaar-pill-tab${activePage === "browse" ? " is-active" : ""}`}
                  >
                    {tab.label}
                  </button>
                  {servicesMenuOpen && !isMobile && (
                    <div
                      onMouseEnter={() => setServicesMenuOpen(true)}
                      onMouseLeave={() => setServicesMenuOpen(false)}
                      className="petaar-nav-dropdown"
                    >
                      {serviceMenuCategories.map((categoryName) => (
                        <button
                          key={`menu-${categoryName}`}
                          type="button"
                          onClick={() => openServiceCategory(categoryName)}
                          className="petaar-nav-dropdown-item"
                        >
                          {categoryName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={tab.label}
                  onClick={() => {
                    setServicesMenuOpen(false);
                    if (typeof tab.onClick === "function") {
                      tab.onClick();
                    }
                  }}
                  className={`petaar-pill-tab${
                    (tab.label === "Home" && activePage === "browse") ||
                    (tab.label === "Marketplace" && activePage === "marketplace")
                      ? " is-active"
                      : ""
                  }`}
                >
                  {tab.label}
                </button>
              )
            ))}
            {role === "provider" && (
              <button
                onClick={onMyServices || onOpenDashboard}
                className="petaar-pill-tab is-active"
              >
                My Services
              </button>
            )}
          </div>
          {isMobile && servicesMenuOpen && (
            <div className="petaar-nav-dropdown petaar-nav-dropdown--mobile">
              {serviceMenuCategories.map((categoryName) => (
                <button
                  key={`mobile-menu-${categoryName}`}
                  type="button"
                  onClick={() => openServiceCategory(categoryName)}
                  className="petaar-nav-dropdown-item"
                >
                  {categoryName}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main style={{ flex: 1, overflowX: "hidden" }}>{children}</main>

      <footer className="petaar-footer">
        <div className="petaar-footer-grid">
          {footerColumns.map((col) => (
            <div
              key={col.title}
              id={
                col.title === "Petaar" ? "site-footer-about" :
                col.title === "Support" ? "site-footer-contact" :
                undefined
              }
            >
              <h4 style={{ marginTop: 0, marginBottom: 10 }}>{col.title}</h4>
              <div style={{ display: "grid", gap: 8 }}>
                {col.links.map((link) => (
                  <button
                    key={`${col.title}-${link.label}`}
                    onClick={() => {
                      if (link.legalKey) onOpenLegal?.(link.legalKey);
                      if (link.onClick) link.onClick();
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "var(--muted)",
                      textAlign: "left",
                      padding: 0,
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div>
            <h4 style={{ marginTop: 0, marginBottom: 10 }}>Connect with us</h4>
            <div style={{ display: "flex", gap: 10 }}>
              {["f", "ig", "yt", "in"].map((item) => (
                <span
                  key={item}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    border: "1px solid rgba(245, 197, 24, 0.24)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    color: "var(--primary-ink)",
                    background: "#fffef7",
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1180, margin: "14px auto 0", color: "var(--muted)", fontSize: 12 }}>
          <div style={{ marginBottom: 6 }}>
            Address: {PETAAR_CONTACT.address}
          </div>
          <div style={{ marginBottom: 8 }}>
            Phone / WhatsApp:{" "}
            <a
              href={`tel:+91${PETAAR_CONTACT.phone}`}
              style={{ color: "var(--primary)", textDecoration: "none", fontWeight: 700 }}
            >
              +91 {PETAAR_CONTACT.phone}
            </a>
          </div>
          Copyright 2026 Petaar Technologies. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
